<template>
  <div class="sr-page">
    <div class="sr-bg"></div>

    <div class="sr-wrap">

      <!-- Loading -->
      <div v-if="state === 'loading'" class="sr-card sr-card--center">
        <div class="sr-spinner"></div>
        <p class="sr-loading-text">Loading survey…</p>
      </div>

      <!-- Error / invalid / expired -->
      <div v-else-if="state === 'error'" class="sr-card sr-card--center">
        <div class="sr-icon-wrap sr-icon-wrap--red">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        </div>
        <h2 class="sr-status-title">{{ errorTitle }}</h2>
        <p class="sr-status-sub">{{ errorMessage }}</p>
      </div>

      <!-- Already responded -->
      <div v-else-if="state === 'already_done'" class="sr-card sr-card--center">
        <div class="sr-icon-wrap sr-icon-wrap--green">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h2 class="sr-status-title">Already submitted</h2>
        <p class="sr-status-sub">You've already responded to this survey. Thank you!</p>
      </div>

      <!-- Survey form -->
      <div v-else-if="state === 'survey'" class="sr-card">
        <div class="sr-brand">
          <div class="sr-brand-dot"></div>
          <span>Mapiibs CRM</span>
        </div>
        <h1 class="sr-survey-name">{{ survey.name }}</h1>
        <p class="sr-question">{{ survey.question }}</p>

        <!-- NPS / numeric scale -->
        <div class="sr-scale">
          <div class="sr-scale-labels">
            <span>{{ survey.scale_min }} — Not at all likely</span>
            <span>{{ survey.scale_max }} — Extremely likely</span>
          </div>
          <div class="sr-scale-buttons">
            <button
              v-for="n in scaleRange"
              :key="n"
              class="sr-scale-btn"
              :class="{
                'sr-scale-btn--selected': form.score === n,
                'sr-scale-btn--low':  survey.type === 'nps' && n <= 6,
                'sr-scale-btn--mid':  survey.type === 'nps' && n >= 7 && n <= 8,
                'sr-scale-btn--high': survey.type === 'nps' && n >= 9,
              }"
              @click="form.score = n"
            >{{ n }}</button>
          </div>
        </div>

        <div class="sr-comment">
          <label class="sr-label">Any additional comments? <span class="sr-optional">(optional)</span></label>
          <textarea v-model="form.comment" class="sr-textarea" rows="3" placeholder="Share your thoughts…"></textarea>
        </div>

        <button
          class="sr-submit"
          :disabled="form.score === null || submitting"
          @click="submit"
        >
          {{ submitting ? 'Submitting…' : 'Submit Feedback' }}
        </button>
        <p v-if="form.score === null" class="sr-hint">Please select a score above to continue</p>
      </div>

      <!-- Thank you -->
      <div v-else-if="state === 'done'" class="sr-card sr-card--center">
        <div class="sr-icon-wrap sr-icon-wrap--green">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h2 class="sr-status-title">Thank you!</h2>
        <p class="sr-status-sub">{{ thankYouMessage }}</p>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';

const route       = useRoute();
const token       = route.params.token;
const companyCode = route.params.companyCode;

const state        = ref('loading');   // loading | survey | done | already_done | error
const survey       = ref({});
const form         = ref({ score: null, comment: '' });
const submitting   = ref(false);
const errorTitle   = ref('');
const errorMessage = ref('');
const thankYouMessage = ref('Thank you for your feedback!');

const scaleRange = computed(() => {
  const min = survey.value.scale_min ?? 0;
  const max = survey.value.scale_max ?? 10;
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
});

const load = async () => {
  try {
    const { data } = await axios.get(`/public/crm/surveys/${companyCode}/fetch/${token}`);
    if (data.status) {
      survey.value = data.data;
      state.value  = 'survey';
    } else if (data.message === 'already_responded') {
      state.value = 'already_done';
    } else {
      errorTitle.value   = 'Survey unavailable';
      errorMessage.value = data.message;
      state.value        = 'error';
    }
  } catch {
    errorTitle.value   = 'Something went wrong';
    errorMessage.value = 'This survey link may be invalid or expired.';
    state.value        = 'error';
  }
};

const submit = async () => {
  if (form.value.score === null) return;
  submitting.value = true;
  try {
    const { data } = await axios.post(`/public/crm/surveys/${companyCode}/respond/${token}`, {
      score:   form.value.score,
      comment: form.value.comment,
    });
    if (data.status) {
      thankYouMessage.value = data.message;
      state.value = 'done';
    } else {
      errorTitle.value   = 'Submission failed';
      errorMessage.value = data.message;
      state.value        = 'error';
    }
  } catch {
    errorTitle.value   = 'Submission failed';
    errorMessage.value = 'Please try again or contact support.';
    state.value        = 'error';
  } finally {
    submitting.value = false;
  }
};

onMounted(load);
</script>

<style scoped>
.sr-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
.sr-bg {
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #e8f0fb 0%, #f0fdf4 50%, #ede9fe 100%);
  z-index: 0;
}
.sr-wrap {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 560px;
}
/* Card */
.sr-card {
  background: #fff;
  border-radius: 18px;
  padding: 40px 36px;
  box-shadow: 0 8px 40px rgba(0,0,0,.10);
}
.sr-card--center {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  padding: 56px 36px;
}
/* Brand */
.sr-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  font-size: 12px;
  font-weight: 700;
  color: #7a90b0;
  text-transform: uppercase;
  letter-spacing: .06em;
}
.sr-brand-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #004AAD;
}
/* Title / question */
.sr-survey-name {
  font-size: 22px;
  font-weight: 800;
  color: #1a2b45;
  margin: 0 0 12px;
  line-height: 1.3;
}
.sr-question {
  font-size: 16px;
  color: #3d5068;
  margin: 0 0 28px;
  line-height: 1.6;
}
/* Scale */
.sr-scale { margin-bottom: 24px; }
.sr-scale-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #a0aec0;
  margin-bottom: 10px;
}
.sr-scale-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.sr-scale-btn {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 2px solid #e8edf5;
  background: #f4f7fb;
  color: #3d5068;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all .15s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sr-scale-btn:hover { border-color: #004AAD; background: #e8f0fb; color: #004AAD; }
.sr-scale-btn--selected { background: #004AAD !important; border-color: #004AAD !important; color: #fff !important; transform: scale(1.1); }
.sr-scale-btn--low  { border-color: #fecaca; background: #fff7f7; color: #dc2626; }
.sr-scale-btn--mid  { border-color: #fde68a; background: #fffbeb; color: #d97706; }
.sr-scale-btn--high { border-color: #bbf7d0; background: #f0fdf4; color: #16a34a; }
/* Comment */
.sr-comment { margin-bottom: 24px; }
.sr-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #1a2b45;
  margin-bottom: 8px;
}
.sr-optional { font-weight: 400; color: #a0aec0; }
.sr-textarea {
  width: 100%;
  border: 1.5px solid #dde5f0;
  border-radius: 10px;
  padding: 10px 13px;
  font-size: 14px;
  color: #1a2b45;
  font-family: inherit;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
}
.sr-textarea:focus { border-color: #004AAD; box-shadow: 0 0 0 3px rgba(0,74,173,.07); }
/* Submit */
.sr-submit {
  width: 100%;
  background: #004AAD;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 13px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s;
}
.sr-submit:hover:not(:disabled) { background: #003b8e; }
.sr-submit:disabled { opacity: .5; cursor: not-allowed; }
.sr-hint { text-align: center; font-size: 12px; color: #a0aec0; margin: 8px 0 0; }
/* Status screens */
.sr-icon-wrap {
  width: 64px; height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}
.sr-icon-wrap svg { width: 32px; height: 32px; }
.sr-icon-wrap--green { background: #dcfce7; color: #16a34a; }
.sr-icon-wrap--red   { background: #fee2e2; color: #dc2626; }
.sr-status-title {
  font-size: 22px;
  font-weight: 800;
  color: #1a2b45;
  margin: 0 0 6px;
}
.sr-status-sub {
  font-size: 15px;
  color: #7a90b0;
  margin: 0;
  line-height: 1.5;
}
/* Spinner */
.sr-spinner {
  width: 36px; height: 36px;
  border: 3px solid #e8edf5;
  border-top-color: #004AAD;
  border-radius: 50%;
  animation: spin .7s linear infinite;
  margin-bottom: 4px;
}
.sr-loading-text { color: #7a90b0; font-size: 14px; margin: 0; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
